// Hooks for registering shortcut handlers, activating scopes, and reading the
// pending chord buffer. Authored IDENTICALLY across apps/web, apps/perps and
// marginx — keep in sync.

import { useContext, useEffect, useRef, useState } from "react";
import { KeyboardContext, type KeyboardContextValue } from "./KeyboardProvider";
import type { ChordToken, Scope, ShortcutHandler } from "./types";

export function useKeyboardContext(): KeyboardContextValue {
  const ctx = useContext(KeyboardContext);
  if (!ctx) throw new Error("useKeyboard* must be used within a <KeyboardProvider>");
  return ctx;
}

/**
 * Register a handler for a shortcut id (from the SHORTCUTS registry). The handler
 * is kept in a ref so you don't need a stable identity; it always sees fresh
 * closures. Return `false` from the handler to let the event pass through.
 */
export function useShortcut(id: string, handler: ShortcutHandler): void {
  const { manager } = useKeyboardContext();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    const fn: ShortcutHandler = (e) => ref.current(e);
    manager.addHandler(id, fn);
    return () => manager.removeHandler(id, fn);
  }, [manager, id]);
}

/**
 * Register one handler against several shortcut ids at once (e.g. the 1–9 tab
 * jumps). The handler reads which key fired from the event, so a single
 * registration serves the whole set without calling `useShortcut` in a loop.
 */
export function useShortcuts(ids: string[], handler: ShortcutHandler): void {
  const { manager } = useKeyboardContext();
  const ref = useRef(handler);
  ref.current = handler;
  const key = ids.join(",");

  useEffect(() => {
    const fn: ShortcutHandler = (e) => ref.current(e);
    ids.forEach((id) => manager.addHandler(id, fn));
    return () => ids.forEach((id) => manager.removeHandler(id, fn));
    // `key` captures the id set; `ids` identity churns each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, key]);
}

/** Activate a scope while the calling component is mounted. */
export function useKeyboardScope(scope: Scope): void {
  const { manager } = useKeyboardContext();
  useEffect(() => {
    manager.pushScope(scope);
    return () => manager.popScope(scope);
  }, [manager, scope]);
}

/** Subscribe to the pending chord buffer (for the hint bar to echo "G …"). */
export function useKeyboardBuffer(): ChordToken[] {
  const { manager } = useKeyboardContext();
  const [buffer, setBuffer] = useState<ChordToken[]>(manager.getBuffer());
  useEffect(() => manager.onBufferChange(setBuffer), [manager]);
  return buffer;
}

/** Whether a scope is currently active (for the hint bar). */
export function useIsScopeActive(scope: Scope): boolean {
  const { manager } = useKeyboardContext();
  // Re-read on buffer changes (cheap proxy for "something keyboard-y happened");
  // scopes change on mount/unmount so a render is already triggered then too.
  useKeyboardBuffer();
  return manager.isScopeActive(scope);
}
